import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { 
    Table, 
    Card, 
    Button, 
    Input, 
    Space, 
    Tag, 
    Modal, 
    Form, 
    Typography, 
    message, 
    Popconfirm,
    Tooltip,
    Switch,
    Select
} from 'antd';
import { 
    SearchOutlined, 
    EditOutlined, 
    DeleteOutlined, 
    PlusOutlined,
    BarChartOutlined,
    SyncOutlined,
    EyeOutlined
} from '@ant-design/icons';
import { useAdmin } from '../../hooks/useAdmin';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;
const { Option } = Select;

const MarketAnalyses = () => {
    const { t } = useTranslation();
    const { loading, getAnalyses, createAnalysis, updateAnalysis, deleteAnalysis } = useAdmin();
    const [analyses, setAnalyses] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingAnalysis, setEditingAnalysis] = useState(null);
    const [form] = Form.useForm();

    const loadAnalyses = useCallback(async () => {
        const result = await getAnalyses();
        if (result.success) {
            setAnalyses(result.data);
        }
    }, [getAnalyses]);

    useEffect(() => {
        loadAnalyses();
    }, [loadAnalyses]);

    const handleAdd = () => {
        setEditingAnalysis(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingAnalysis(record);
        form.setFieldsValue(record);
        setIsModalVisible(true);
    };

    const handleDelete = async (id) => {
        const result = await deleteAnalysis(id);
        if (result.success) {
            message.success(t('admin.market_analyses.messages.delete_success'));
            loadAnalyses();
        }
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            let result;

            if (editingAnalysis) {
                result = await updateAnalysis(editingAnalysis.id, values);
            } else {
                result = await createAnalysis(values);
            }

            if (result.success) {
                message.success(editingAnalysis ? t('admin.market_analyses.messages.update_success') : t('admin.market_analyses.messages.create_success'));
                setIsModalVisible(false);
                loadAnalyses();
            }
        } catch (error) {
            console.error('Validate Failed:', error);
        }
    };

    const filteredData = useMemo(() => {
        return analyses.filter(item => 
            item.title.toLowerCase().includes(searchText.toLowerCase()) || 
            (item.summary && item.summary.toLowerCase().includes(searchText.toLowerCase()))
        );
    }, [analyses, searchText]);

    const columns = [
        {
            title: t('admin.market_analyses.table.title'),
            dataIndex: 'title',
            key: 'title',
            render: (text) => <Text strong style={{ color: '#6366f1' }}>{text}</Text>,
        },
        {
            title: t('admin.market_analyses.table.category'),
            dataIndex: 'category',
            key: 'category',
            render: (cat) => <Tag color="blue">{cat?.toUpperCase() || 'GENERAL'}</Tag>,
        },
        {
            title: t('admin.market_analyses.table.author'),
            dataIndex: 'author_name',
            key: 'author_name',
            render: (name) => <Tag icon={<EyeOutlined />}>{name || 'Admin'}</Tag>,
        },
        {
            title: t('admin.market_analyses.table.date'),
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => new Date(date).toLocaleDateString(),
        },
        {
            title: t('admin.market_analyses.table.status'),
            dataIndex: 'is_published',
            key: 'is_published',
            render: (pub) => <Tag color={pub ? 'green' : 'orange'}>{pub ? t('admin.market_analyses.table.published') : t('admin.market_analyses.table.draft')}</Tag>,
        },
        {
            title: t('admin.market_analyses.table.actions'),
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Tooltip title={t('common.edit') || 'Edit'}>
                        <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} style={{ color: '#6366f1' }} />
                    </Tooltip>
                    <Popconfirm
                        title={t('admin.market_analyses.messages.delete_confirm')}
                        onConfirm={() => handleDelete(record.id)}
                        okText={t('common.yes') || 'Yes'}
                        cancelText={t('common.no') || 'No'}
                    >
                        <Tooltip title={t('common.delete') || 'Delete'}>
                            <Button type="text" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="admin-analyses-inner">
            <div className="page-title-section" style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={2} style={{ margin: 0, color: '#f1f5f9' }}>
                        <BarChartOutlined style={{ marginRight: 12, color: '#6366f1' }} />
                        {t('admin.market_analyses.title')}
                    </Title>
                    <Text type="secondary">{t('admin.market_analyses.subtitle')}</Text>
                </div>
                <Space>
                    <Button icon={<SyncOutlined spin={loading} />} onClick={loadAnalyses}>{t('admin.market_analyses.refresh')}</Button>
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />} 
                        onClick={handleAdd}
                        style={{ borderRadius: 8, height: 40, background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', border: 'none' }}
                    >
                        {t('admin.market_analyses.new_analysis')}
                    </Button>
                </Space>
            </div>

            <Card className="glass-card" style={{ background: 'rgba(30, 41, 59, 0.4)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                    <Input
                        placeholder={t('admin.market_analyses.search_placeholder')}
                        prefix={<SearchOutlined />}
                        style={{ width: 300, borderRadius: 8 }}
                        onChange={e => setSearchText(e.target.value)}
                        allowClear
                    />
                </div>

                <Table 
                    columns={columns} 
                    dataSource={filteredData} 
                    rowKey="id"
                    loading={loading}
                    className="admin-table"
                />
            </Card>

            <Modal
                title={editingAnalysis ? t('admin.market_analyses.form.edit_title') : t('admin.market_analyses.form.new_title')}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={() => setIsModalVisible(false)}
                okText={t('admin.market_analyses.form.ok_text')}
                cancelText={t('admin.market_analyses.form.cancel_text')}
                width={800}
                centered
            >
                <Form form={form} layout="vertical">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <Form.Item
                            name="title"
                            label={t('admin.market_analyses.form.title_label')}
                            rules={[{ required: true }]}
                        >
                            <Input />
                        </Form.Item>

                        <Form.Item
                            name="category"
                            label={t('admin.market_analyses.form.category_label')}
                            initialValue="crypto"
                        >
                            <Select>
                                <Option value="crypto">Crypto</Option>
                                <Option value="forex">Forex</Option>
                                <Option value="stocks">Actions</Option>
                                <Option value="macro">Macro-économie</Option>
                            </Select>
                        </Form.Item>
                    </div>

                    <Form.Item
                        name="summary"
                        label={t('admin.market_analyses.form.summary_label')}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="content"
                        label={t('admin.market_analyses.form.content_label')}
                        rules={[{ required: true }]}
                    >
                        <Input.TextArea rows={8} />
                    </Form.Item>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <Form.Item
                            name="image_url"
                            label={t('admin.market_analyses.form.image_url')}
                        >
                            <Input />
                        </Form.Item>

                        <Form.Item
                            name="is_published"
                            label={t('admin.market_analyses.form.publish_now')}
                            valuePropName="checked"
                            initialValue={true}
                        >
                            <Switch />
                        </Form.Item>
                    </div>
                </Form>
            </Modal>

            <style dangerouslySetInnerHTML={{ __html: `
                .admin-page-container { animation: fadeIn 0.5s ease-out; }
                .glass-card { box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37); }
                .admin-table .ant-table { background: transparent !important; }
                .admin-table .ant-table-thead > tr > th { background: rgba(15, 23, 42, 0.8) !important; color: #94a3b8 !important; border-bottom: 1px solid rgba(255,255,255,0.05) !important; }
                .admin-table .ant-table-tbody > tr > td { border-bottom: 1px solid rgba(255,255,255,0.02) !important; }
                .admin-table .ant-table-tbody > tr:hover > td { background: rgba(99, 102, 241, 0.05) !important; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}} />
        </div>
    );
};

export default MarketAnalyses;
